import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { PlayCircle, FileText, Radio, CheckCircle2, ExternalLink, ChevronLeft, Award, Circle } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { CertificateButton, ProgressBar } from "@/components/CertificateButton";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

export default function CoursePlayer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [active, setActive] = useState(null);
  const [error, setError] = useState("");
  const [prog, setProg] = useState({ completed_lessons: [], progress: 0, certificate: null });

  const allLessons = c?.sections?.flatMap((s) => s.lessons || []) || [];
  const isDone = (l) => prog.completed_lessons.includes(l.id);
  const toggleDone = async (l) => {
    try {
      const { data } = await api.post(`/learn/${id}/lessons/${l.id}/complete`, { undo: isDone(l) });
      if (data.certificate && !prog.certificate) toast.success("Congratulations! You completed the course and earned your certificate.");
      setProg(data);
      const next = allLessons[allLessons.findIndex((x) => x.id === l.id) + 1];
      if (!isDone(l) && next) setActive(next);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update progress"); }
  };

  useEffect(() => {
    api.get(`/learn/${id}`).then(({ data }) => {
      setC(data);
      setProg({ completed_lessons: data.completed_lessons || [], progress: data.progress || 0, certificate: data.certificate });
      const first = data.sections?.[0]?.lessons?.[0];
      if (first) setActive(first);
    }).catch((e) => setError(e.response?.data?.detail || "Access denied"));
  }, [id]);

  if (error) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4">
      <p className="text-crimson font-semibold">{error}</p>
      <Button onClick={() => navigate("/learner/my-courses")}>Back to My Courses</Button>
    </div>
  );
  if (!c) return <div className="min-h-screen flex items-center justify-center"><div className="h-10 w-10 border-4 border-amber border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-navy text-white" data-testid="course-player">
      <div className="border-b border-white/10 px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate("/learner/my-courses")} className="p-2 hover:bg-white/10 rounded"><ChevronLeft size={20} /></button>
        <h1 className="font-heading font-semibold truncate flex-1">{c.title}</h1>
        <div className="w-32 sm:w-48 hidden sm:block"><ProgressBar dark value={prog.progress} testid="player-progress" /></div>
        {prog.certificate && <CertificateButton cert={prog.certificate} testid="player-certificate" />}
      </div>
      {prog.certificate && (
        <div className="bg-crimson/15 border-b border-crimson/30 px-4 py-3 flex items-center gap-3 text-sm" data-testid="course-complete-banner">
          <Award size={18} className="text-crimson shrink-0" />
          <span className="flex-1">You've completed this course. Your VIRIKS certificate <span className="font-mono text-white/70">{prog.certificate.cert_no}</span> is ready.</span>
        </div>
      )}
      <div className="grid lg:grid-cols-4">
        <div className="lg:col-span-3 p-4 lg:p-8">
          {active ? (
            <div>
              <div className="aspect-video bg-black rounded-xl overflow-hidden">
                {active.video_url && (active.video_url.includes("/api/files/") || /\.(mp4|webm|mov|ogg)$/i.test(active.video_url))
                  ? <video key={active.id} src={fileUrl(active.video_url)} controls className="w-full h-full" />
                  : <iframe title={active.title} src={active.video_url} className="w-full h-full" allowFullScreen />}
              </div>
              <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
                <h2 className="font-heading text-2xl font-bold">{active.title}</h2>
                <Button onClick={() => toggleDone(active)} variant={isDone(active) ? "outline" : "default"}
                  className={isDone(active) ? "bg-transparent border-white/30 text-white hover:bg-white/10" : "viriks-amber text-white font-semibold"} data-testid="mark-complete">
                  <CheckCircle2 size={16} className="mr-1" /> {isDone(active) ? "Completed — undo" : "Mark as complete"}
                </Button>
              </div>
              {active.resource_url && (
                <a href={fileUrl(active.resource_url)} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-2 text-sm text-white/80 hover:text-white underline" data-testid="lesson-resource"><FileText size={15} /> Download lesson notes</a>
              )}
              {active.notes && (
                <div className="mt-4 bg-white/5 border border-white/10 rounded-lg p-5">
                  <h3 className="font-semibold flex items-center gap-2"><FileText size={16} /> Lesson Notes</h3>
                  <p className="text-white/70 mt-2 text-sm">{active.notes}</p>
                </div>
              )}
            </div>
          ) : <p className="text-white/60">Select a lesson to begin.</p>}

          {c.live_classes?.length > 0 && (
            <div className="mt-8">
              <h3 className="font-heading text-lg font-bold flex items-center gap-2 mb-4"><Radio size={18} className="text-crimson" /> Live Classes</h3>
              <div className="space-y-2">
                {c.live_classes.map((l) => (
                  <div key={l.id} className="bg-white/5 border border-white/10 rounded-lg p-4 flex items-center justify-between">
                    <div><div className="font-semibold text-sm">{l.title}</div><div className="text-xs text-white/60">{l.date} {l.start_time}</div></div>
                    {l.meeting_url && <a href={l.meeting_url} target="_blank" rel="noreferrer"><Button size="sm" className="viriks-amber text-white">Join <ExternalLink size={13} className="ml-1" /></Button></a>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <aside className="border-t lg:border-t-0 lg:border-l border-white/10 p-4 max-h-[calc(100vh-57px)] overflow-y-auto">
          <h3 className="font-heading font-semibold mb-1">Course Content</h3>
          <p className="text-xs text-white/60 mb-3">{prog.completed_lessons.length} of {allLessons.length} lessons completed</p>
          <div className="sm:hidden mb-3"><ProgressBar dark value={prog.progress} /></div>
          <Accordion type="multiple" defaultValue={c.sections?.map((s) => s.id)} className="space-y-2">
            {c.sections?.map((s) => (
              <AccordionItem key={s.id} value={s.id} className="border-white/10">
                <AccordionTrigger className="text-sm hover:no-underline">{s.title}</AccordionTrigger>
                <AccordionContent>
                  {s.lessons?.map((l) => (
                    <button key={l.id} onClick={() => setActive(l)} data-testid={`lesson-${l.id}`}
                      className={`w-full flex items-center gap-2 text-left px-3 py-2 rounded text-sm transition-colors ${active?.id === l.id ? "bg-amber text-navy" : "text-white/70 hover:bg-white/10"}`}>
                      {isDone(l) ? <CheckCircle2 size={15} className={active?.id === l.id ? "" : "text-emerald-400"} data-testid={`lesson-done-${l.id}`} /> : active?.id === l.id ? <PlayCircle size={15} /> : <Circle size={15} />} <span className="flex-1 truncate">{l.title}</span> <span className="text-xs">{l.duration}</span>
                    </button>
                  ))}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </aside>
      </div>
    </div>
  );
}
